// Scorecards weekly analysis, PR 1 (2026-10-19, Patrick's ask): the
// fixed-rule findings from GET /api/admin/reporting/scorecards/insights,
// shown as plain text and a small table, no AI yet. In PR 2 this becomes the
// collapsible "Show the numbers" section under the AI write-up, so it is kept
// self-contained here and is easy to remove. Follows the Scorecards Team
// chips (`teams`, null for All). ADH% is never part of it (Patrick's rule).
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Collapse, FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { fetchScorecardsInsights, fetchScorecardsWriteup, generateScorecardsWriteup } from '../api.js'
import { writeupViewFor, hasMissingWriteup, hasNoActivity, numbersStartOpen, writtenStamp } from '../scorecardsWriteup.js'
import { intentLabel } from '../constants/intents.js'
import SectionHeading from './SectionHeading.jsx'

function fmtMin(m) {
  if (m == null) return '—'
  const total = Math.round(m * 60)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
const fmtPct = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`)
const fmtGap = (x) => (x == null ? '' : `${x >= 0 ? '+' : ''}${Math.round(x * 100)}%`)

function delta(cur, prev) {
  if (cur == null || prev == null) return ''
  const d = cur - prev
  return ` (${d >= 0 ? '+' : '−'}${fmtMin(Math.abs(d))})`
}

// Who the agent was compared with: their own team, or every agent when the
// team is too small to be its own bar (MIN_TEAM_PEERS, server side).
const barLabel = (comparedWith) => (comparedWith === 'all_agents' ? 'all agents' : 'team')

const onlyWeek = (f) => (f.onlyWeekWithData ? ' Only week in the last 3 with 20+ contacts, so no earlier week to compare.' : '')
const TIER_LABEL = { high: 'High AHT', sustained: 'Sustained gap', watch: 'Watch' }
const fmtChange = (x) => (x == null ? '' : `${x >= 0 ? '+' : '−'}${Math.abs(Math.round(x * 100))}%`)

// "6:47, 5:35, 4:45, 4:27" for the last 4 weeks, oldest first; a week without
// enough contacts shows as a dash.
const trendLine = (t) => t.weeks.map((w) => (w.contacts >= 20 ? fmtMin(w.ahtMinutes) : '—')).join(' → ')

function findingText(f) {
  switch (f.type) {
    case 'high_aht':
      return `High AHT: ${fmtMin(f.emailAhtMinutes)} vs. all agents ${fmtMin(f.allAgentsMinutes)} this week (${fmtGap(f.gapPct)}, ${f.mixAdjustedGapPct == null ? 'no mix-adjusted figure' : `${fmtGap(f.mixAdjustedGapPct)} on the same contact mix`}), ${f.contacts} contacts.`
    case 'intent_aht_gap':
      return `${intentLabel(f.intent)}: ${fmtMin(f.agentMinutes)} vs. ${barLabel(f.comparedWith)} ${fmtMin(f.teamMinutes)} (${fmtGap(f.gapPct)}) across ${f.contacts} contacts; flagged ${f.weeksFlagged.length} of the last 3 weeks.${onlyWeek(f)}`
    case 'mix_adjusted_aht_gap':
      return `Email AHT ${fmtMin(f.emailAhtMinutes)} vs. ${fmtMin(f.expectedAhtMinutes)} expected for their contact mix, based on ${barLabel(f.comparedWith)} (${fmtGap(f.gapPct)}), ${f.contacts} contacts; flagged ${f.weeksFlagged.length} of the last 3 weeks.${onlyWeek(f)}`
    case 'support_email_aht_gap':
      return `Support Email AHT ${fmtMin(f.agentMinutes)} vs. team ${fmtMin(f.teamMinutes)} (${fmtGap(f.gapPct)}), ${f.contacts} contacts; flagged ${f.weeksFlagged.length} of the last 3 weeks.`
    case 'low_utilization':
      return `Utilization ${fmtPct(f.utilization)} over ${f.scheduledHours} scheduled hours, flagged ${f.weeksFlagged.length} of the last 3 weeks. ${f.note}`
    default:
      return f.type
  }
}

export default function ScorecardsFocusPanel({ teams, notice }) {
  const [weekEnding, setWeekEnding] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const teamsKey = teams ? teams.join(',') : ''
  // The AI write-up (PR 2): null until loaded; { enabled: false } when the flag is off.
  const [writeup, setWriteup] = useState(null)
  const [writing, setWriting] = useState(false)
  const [writingTeam, setWritingTeam] = useState(null) // the team a Write again is running for
  const [writeupError, setWriteupError] = useState(null)
  const [showNumbers, setShowNumbers] = useState(null) // null = follow the default

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchScorecardsInsights({ weekEnding: weekEnding ?? undefined, teams: teams ?? undefined })
      .then((r) => { if (!cancelled) setData(r) })
      .catch((e) => { if (!cancelled) { setError(e.message); setData(null) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekEnding, teamsKey])

  const shownWeek = data?.weekEnding ?? null
  useEffect(() => {
    if (!shownWeek) return undefined
    let cancelled = false
    setWriteupError(null)
    fetchScorecardsWriteup({ weekEnding: shownWeek })
      .then((r) => { if (!cancelled) setWriteup(r) })
      .catch(() => { if (!cancelled) setWriteup(null) }) // the findings below still stand alone
    return () => { cancelled = true }
  }, [shownWeek])

  const writeupOn = writeup?.enabled === true
  const numbersOpen = showNumbers ?? numbersStartOpen(writeupOn)
  const shownTeams = (data?.teams ?? []).map((t) => t.team)

  const writeNow = async (body) => {
    setWriting(true)
    setWritingTeam(body.team ?? null)
    setWriteupError(null)
    try {
      const r = await generateScorecardsWriteup({ weekEnding: shownWeek, ...body })
      setWriteup(r)
      // A team's model call that failed writes nothing, so say why instead of leaving "Not written yet".
      if (r?.run?.failed > 0) {
        const why = (r.run.errors ?? []).map((x) => `${x.team}: ${x.message}`).join('; ')
        setWriteupError(`${r.run.failed} write-up${r.run.failed === 1 ? '' : 's'} failed${why ? ` (${why})` : ''}. Click again to retry.`)
      }
    } catch (e) {
      setWriteupError(e.message)
    } finally {
      setWriting(false)
    }
  }

  const hasSupport = data?.teamOverTeam?.some((t) => 'supportEmailAhtMinutes' in t.current)

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 420px', maxWidth: 760, minWidth: 0, alignSelf: 'flex-start', mt: 7 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ rowGap: 1, mb: 1 }}>
        <Typography variant="subtitle1">{writeupOn ? 'Weekly analysis' : 'Weekly analysis: focus areas (preview)'}</Typography>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Week ending</InputLabel>
          <Select label="Week ending" value={weekEnding ?? data?.weekEnding ?? ''} onChange={(e) => setWeekEnding(e.target.value)}>
            {(data?.availableWeeks ?? []).map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      <SectionHeading variant="caption" appearance="quiet" sx={{ mb: 0.5 }} title="About this analysis">
        Last completed week compared with the 4 before it, with 20+ contacts (10+ for one intent). Three tiers.
        High AHT: this week 20% or more above every other agent's average, no mix adjustment. Sustained gap: 20% above
        the rest of their team on the same intents, so a harder contact mix isn't counted against anyone, in at least 2
        of the last 3 weeks (one week is enough when it is the only week with enough contacts). Watch: at or above the all-agent
        average this week, and above it in 3 of the last 4 weeks or moved 15% or more over 4 weeks; shown, never a flag. Utilization
        only with 8+ scheduled hours. Supervisors and ADH% are not analyzed.
      </SectionHeading>
      {/* The caution stays visible: it is not a description. */}
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>{notice}</Typography>

      {writeupOn && data && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            Written Monday at 7 AM ET from the numbers below, one paragraph per team. It describes what the numbers
            show and never gives advice. Per-agent times are claimed time, not effort.
          </Typography>
          {shownTeams.map((team) => {
            const view = writeupViewFor(team, writeup.teams)
            const noContacts = hasNoActivity(data.teamOverTeam, team)
            return (
              <Box key={team} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">{team}</Typography>
                {view.kind === 'ready' && (
                  <>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{view.narrative}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">Written {writtenStamp(view.generatedAt)}</Typography>
                      <Button size="small" disabled={writing} onClick={() => writeNow({ team, force: true })}>
                        {writing && writingTeam === team ? 'Writing...' : 'Write again'}
                      </Button>
                    </Stack>
                  </>
                )}
                {view.kind === 'held' && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      No write-up: the text was held back{view.heldReason ? ` (${view.heldReason})` : ''}. The numbers below stand on their own.
                    </Typography>
                    <Button size="small" disabled={writing} onClick={() => writeNow({ team, force: true })}>
                      {writing && writingTeam === team ? 'Writing...' : 'Write again'}
                    </Button>
                  </>
                )}
                {view.kind === 'none' && (
                  <Typography variant="body2" color="text.secondary">
                    {noContacts ? 'No CXHub email contacts this week or last, so nothing to write up.' : 'Not written yet.'}
                  </Typography>
                )}
              </Box>
            )
          })}
          {hasMissingWriteup(shownTeams.filter((t) => !hasNoActivity(data.teamOverTeam, t)), writeup.teams) && (
            <Button size="small" variant="outlined" disabled={writing} onClick={() => writeNow({})}>
              {writing ? 'Writing...' : 'Write it now'}
            </Button>
          )}
          {writeupError && <Alert severity="error" sx={{ mt: 1 }}>{writeupError}</Alert>}
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setShowNumbers(!numbersOpen)}>{numbersOpen ? 'Hide the numbers' : 'Show the numbers'}</Button>
          </Box>
        </Box>
      )}

      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : data ? (
        <Collapse in={numbersOpen} unmountOnExit>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell align="right">Emails</TableCell>
                <TableCell align="right">Email AHT (vs. last wk)</TableCell>
                <TableCell align="right">4-wk avg AHT</TableCell>
                {hasSupport && <TableCell align="right">Support Email AHT</TableCell>}
                <TableCell align="right">Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.teamOverTeam.map((t) => (
                <TableRow key={t.team}>
                  <TableCell>{t.team}</TableCell>
                  <TableCell align="right">{t.current.contacts}</TableCell>
                  <TableCell align="right">{fmtMin(t.current.emailAhtMinutes)}{delta(t.current.emailAhtMinutes, t.previousWeek?.emailAhtMinutes)}</TableCell>
                  <TableCell align="right">{fmtMin(t.priorFourWeekMean.emailAhtMinutes)}</TableCell>
                  {hasSupport && <TableCell align="right">{'supportEmailAhtMinutes' in t.current ? fmtMin(t.current.supportEmailAhtMinutes) : ''}</TableCell>}
                  <TableCell align="right">{fmtPct(t.current.utilization)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.supervisorHandled.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Supervisor-handled (not analyzed): {data.supervisorHandled.map((s) => `${s.agent} ${s.contacts} emails${s.supportEmailContacts ? `, ${s.supportEmailContacts} Support Email` : ''}`).join('; ')}
            </Typography>
          )}

          {data.teams.map((t) => (
            <Box key={t.team} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">{t.team}</Typography>
              {t.comparedWith === 'all_agents' && (
                <Typography variant="body2" color="text.secondary">
                  {(data.teamOverTeam.find((o) => o.team === t.team)?.current?.contacts ?? 0) === 0
                    ? 'No CXHub email contacts this week.'
                    : 'Fewer than 3 agents on this team handled 20+ CXHub contacts this week, so they are compared with all agents.'}
                </Typography>
              )}
              {t.focus.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No focus areas this week.</Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {t.focus.map((a) => (
                    <li key={a.agent}>
                      <Typography variant="body2"><strong>{a.agent}</strong> ({a.contacts} emails)</Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {a.findings.map((f, i) => <li key={i}><Typography variant="body2">{findingText(f)}</Typography></li>)}
                      </Box>
                    </li>
                  ))}
                </Box>
              )}
              {t.focusOverflow > 0 && (
                <Typography variant="body2" color="text.secondary">{t.focusOverflow} more below the top 3.</Typography>
              )}
              {t.focus.some((a) => a.trend?.changePct != null) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Last 4 weeks, oldest first: {t.focus.filter((a) => a.trend).map((a) => `${a.agent} ${trendLine(a.trend)}${a.trend.changePct != null ? ` (${fmtChange(a.trend.changePct)})` : ''}`).join('; ')}
                </Typography>
              )}
              {(t.watch ?? []).length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Watch: {t.watch.map((w) => `${w.agent} ${trendLine(w)}${w.changePct != null ? ` (${fmtChange(w.changePct)})` : ''}, above all agents in ${w.weeksAboveAllAgents} of ${w.weeksWithEnoughContacts} weeks`).join('; ')}
                </Typography>
              )}
              {t.explainedByMix.length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Explained by mix: {t.explainedByMix.map((m) => `${m.agent} (Email AHT ${fmtMin(m.emailAhtMinutes)} vs. ${barLabel(m.comparedWith)} ${fmtMin(m.teamAhtMinutes)}, but ${fmtGap(m.mixAdjustedGapPct)} once mix is accounted for${m.drivers.length ? `; more ${m.drivers.map((d) => intentLabel(d.intent)).join(', ')}` : ''})`).join('; ')}
                </Typography>
              )}
              {t.notEnoughContacts.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Not enough contacts to judge: {t.notEnoughContacts.map((n) => `${n.agent} (${n.contacts}${n.ahtMinutes != null ? `, AHT ${fmtMin(n.ahtMinutes)}` : ''})`).join(', ')}
                </Typography>
              )}
              {t.dataNotes.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Data notes: {t.dataNotes.map((n) => n.type === 'utilization_over_100'
                    ? `${n.agent} utilization ${fmtPct(n.utilization)} (overlap, not a signal)`
                    : `${n.agent} utilization not analyzed (${n.scheduledHours} scheduled hours)`).join('; ')}
                </Typography>
              )}
              {(t.ranking ?? []).length > 0 && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">Contacts</TableCell>
                      <TableCell align="right">AHT</TableCell>
                      <TableCell align="right">All agents</TableCell>
                      <TableCell align="right">vs. all</TableCell>
                      <TableCell align="right">Same mix</TableCell>
                      <TableCell align="right">Extra min</TableCell>
                      <TableCell>Tier</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {t.ranking.map((r) => (
                      <TableRow key={r.agent}>
                        <TableCell>{r.agent}</TableCell>
                        <TableCell align="right">{r.contacts}</TableCell>
                        <TableCell align="right">{fmtMin(r.ahtMinutes)}</TableCell>
                        <TableCell align="right">{fmtMin(r.allAgentsMinutes)}</TableCell>
                        <TableCell align="right">{fmtGap(r.gapPct)}</TableCell>
                        <TableCell align="right">{fmtGap(r.mixAdjustedGapPct)}</TableCell>
                        <TableCell align="right">{Math.round(r.excessMinutes)}</TableCell>
                        <TableCell>{r.tiers.map((x) => TIER_LABEL[x]).join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          ))}
        </Collapse>
      ) : null}
    </Paper>
  )
}
